import { createContext, useContext, useState, useMemo, useEffect, ReactNode, useRef, useCallback } from "react"
import { useRoute, useLocation } from "wouter"
import { useEventListeners, EventListeners, EventListenerMap } from "@/hooks/useEventListeners"
import { useIdEventListener, IdEventListener } from "@/hooks/useEventListeners"
import { useSessionState } from "@/hooks/useSessionState"
import type { LayoutBase, TabData, TabBase, PanelData, DropDirection } from "rc-dock"
import {
  findTabById,
  findPanelByTabId,
  findFirstPanel,
  getActiveTabId as getActiveTabIdFromLayout,
  getMaximizedTabId as getMaximizedTabIdFromLayout,
  getAllTabIds as getAllTabIdsFromLayout,
  isTabOpen as isTabOpenInLayout,
  addTabToPanel,
  removeTab,
  setActiveTab,
  maximizePanel,
  splitPanel,
  findSiblingPanel,
} from "@/chat2/layoutUtils"

// ============================================================================
// TYPES
// ============================================================================

/** Tab types supported by the system */
export type TabType = 
  | "welcome"        // Welcome screen (shown when no tabs open)
  | "workspace"      // Chat workspace (room)
  | "assets"         // File browser
  | "changelog"      // Changelog viewer
  | "webpage"        // Web app panel
  | "profile"        // User profile
  | "subscription"   // Subscription management
  | "integration"    // Integration settings
  | "admin-logs"     // Admin: logs
  | "admin-perf-analyzer" // Admin: perf analyzer
  | "admin-users"    // Admin: user management
  | "admin-user-console" // Admin: detached user console (per-user)
  | "admin-roles"    // Admin: role management
  | "admin-model-profiles" // Admin: model profiles
  | "admin-provider-profiles" // Admin: provider profiles (API keys, costs, limits)
  | "admin-plans"    // Admin: subscription plans
  | "admin-settings" // Admin: platform settings
  | "admin-workspace-defaults" // Admin: workspace defaults
  | "admin-default-prompts" // Admin: default prompts configuration
  | "admin-welcome-settings" // Admin: welcome screen / lobby settings
  | "admin-fetch-settings"   // Admin: fetch tool settings
  | "admin-budget-defaults"  // Admin: context budget band defaults
  | "admin-ponder-defaults"  // Admin: ponder cycle defaults
  | "admin-metrics"          // Admin: platform-wide metrics (usage, cost, errors)
  | "wiki-scope"             // Neural Wiki scope home (personal, system)
  | "wiki-page"              // Individual wiki page view/edit
  | "file-preview"           // File preview (image, text, etc.)
  | "preview-browser"        // Mini browser for HTML asset preview (toolbar + iframe)

/** Extended tab data with our metadata */
export interface Chat2TabData {
  id: string
  tabType: TabType
  entityId?: string  // ID of the entity (workspace ID, etc.)
}

/** Tab metadata (title and icon) stored per tab */
export interface TabMetadata {
  title: ReactNode
  icon?: ReactNode
  onClose?: () => void  // Called when tab is explicitly closed
  dotIntent?: "primary" | "secondary" | "success" | "warning" | "danger" | "info"  // Show colored dot indicator
}

/** Events for per-tab metadata updates (ID-scoped) */
export interface TabMetadataEvents extends EventListenerMap {
  updated: (meta: TabMetadata) => void
}

export type TabPlacement = "split-right" | "split-left" | "split-top" | "split-bottom";
export type TabPlacementScope = "parent" | "root";

export type TabOpenOptions = {
  /** Tab ID to anchor the split relative to */
  relativeTo: string;
  /** Which edge to place the new tab */
  placement: TabPlacement;
  /** "parent" = split the panel containing relativeTo. "root" = split at the root dockbox level. */
  scope: TabPlacementScope;
  /** If true and a panel already exists on the target edge, add the tab there instead of splitting again. */
  reuseExisting?: boolean;
};

/** Persisted layout state — single source of truth */
export interface PersistedLayoutState {
  normalLayout: LayoutBase           // Always the non-maximized layout
  maximizedLayout: LayoutBase | null // Current maximized state (null = not maximized)
  activePanelId: string | null       // Which panel is focused for new tabs
}

/** Welcome tab ID */
export const WELCOME_TAB_ID = "welcome:0"

/** Get the default layout (welcome tab only) */
function getDefaultLayout(): LayoutBase {
  return {
    dockbox: {
      mode: "horizontal",
      children: [
        {
          id: "main-panel",
          tabs: [{ id: WELCOME_TAB_ID }],
          activeId: WELCOME_TAB_ID,
          group: "default",
        },
      ],
    },
  }
}

/** Get the default persisted layout state */
function getDefaultLayoutState(): PersistedLayoutState {
  return {
    normalLayout: getDefaultLayout(),
    maximizedLayout: null,
    activePanelId: null,
  }
}

/** Events for tab operations (kept minimal — most operations are now direct API calls) */
export type TabsEvents = {
  /** Request layout save (handled by ChatLayout) */
  requestLayoutSave: () => void
}

export type TabsContextType = {
  /** Whether context is ready */
  loading: boolean
  /** Event emitter/subscriber for tab operations */
  events: EventListeners<TabsEvents>
  
  // ============================================================================
  // LAYOUT GETTERS
  // ============================================================================
  
  /** Get the current layout to render (maximized or normal) */
  getLayout: () => LayoutBase
  /** Get the active panel ID for new tab placement */
  getActivePanelId: () => string | null
  /** Get the maximized tab ID (null if not maximized) */
  getMaximizedTabId: () => string | null
  /** Check if currently maximized */
  isMaximized: () => boolean
  
  // ============================================================================
  // LAYOUT MANIPULATION
  // ============================================================================
  
  /** Open or focus a tab */
  open: (type: TabType, entityId?: string, options?: TabOpenOptions) => void
  /** Close a tab by ID */
  close: (tabId: string) => void
  /** Set the layout (called by ChatLayout on onLayoutChange, also syncs URL) */
  setLayout: (layout: LayoutBase, currentTabId?: string, direction?: DropDirection) => void
  /** Set the active panel (for new tab placement) */
  setActivePanel: (panelId: string | null) => void
  
  // ============================================================================
  // MAXIMIZE
  // ============================================================================
  
  /** Maximize a tab (creates maximized layout) */
  maximize: (tabId: string) => void
  /** Restore from maximized state */
  restoreMaximized: () => void
  
  // ============================================================================
  // LAYOUT QUERIES
  // ============================================================================
  
  /** Find a tab by ID */
  findTab: (tabId: string) => TabData | null
  /** Find the panel containing a tab */
  findPanel: (tabId: string) => PanelData | null
  /** Get the currently active tab ID */
  getActiveTabId: () => string | null
  /** Get all tab IDs */
  getAllTabIds: () => string[]
  /** Check if a tab is open */
  isTabOpen: (tabId: string) => boolean
  
  // ============================================================================
  // TAB METADATA
  // ============================================================================
  
  /** ID-scoped events for per-tab metadata updates */
  metadataEvents: IdEventListener<TabMetadataEvents>
  /** Get tab metadata (title, icon) - synchronous, reads from cache */
  getTabMetadata: (tabId: string) => TabMetadata | undefined
  /** Set tab metadata - stores in cache and emits event to that tab only */
  setTabMetadata: (tabId: string, meta: TabMetadata) => void
}

// ============================================================================
// HELPERS
// ============================================================================

/** Generate a tab ID from type and entity ID */
function makeTabId(type: TabType, entityId?: string): string {
  return entityId ? `${type}:${entityId}` : type
}

/** Parse a tab ID into type and entity ID */
function parseTabId(tabId: string): { type: TabType; entityId?: string } {
  const [type, entityId] = tabId.split(":")
  return { type: type as TabType, entityId }
}

/** Convert tab ID to route path */
function tabIdToPath(tabId: string | null): string {
  if (!tabId) return "/"
  const { type, entityId } = parseTabId(tabId)
  return entityId ? `/t/${type}/${entityId}` : `/t/${type}`
}



// ============================================================================
// CONTEXT
// ============================================================================

const TabsContext = createContext<TabsContextType | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

export function TabsProvider({ children }: { children: ReactNode }) {
  // Wouter routing - /t/:type/:id? pattern
  const [routeMatch, routeParams] = useRoute("/t/:type/:id?")
  const [, navigate] = useLocation()
  const routeType = routeParams?.type
  const routeId = routeParams?.id
  
  // Events for tab operations (minimal now)
  const events = useEventListeners<TabsEvents>("tabs")
  
  // ID-scoped events for per-tab metadata updates
  const metadataEvents = useIdEventListener<TabMetadataEvents>("tabMetadata")
  
  // Non-reactive metadata storage (ref, not state - prevents global re-renders)
  const metadataRef = useRef<Map<string, TabMetadata>>(new Map())
  
  // ============================================================================
  // LAYOUT STATE — SINGLE SOURCE OF TRUTH
  // ============================================================================
  
  const [layoutState, setLayoutState] = useSessionState<PersistedLayoutState>(
    "dock-layout-v2",
    getDefaultLayoutState()
  )
  
  const [loading, setLoading] = useState(true)
  
  // ============================================================================
  // LAYOUT GETTERS
  // ============================================================================
  
  const getLayout = useCallback((): LayoutBase => {
    return layoutState.maximizedLayout ?? layoutState.normalLayout
  }, [layoutState.maximizedLayout, layoutState.normalLayout])
  
  const getActivePanelId = useCallback((): string | null => {
    return layoutState.activePanelId
  }, [layoutState.activePanelId])
  
  const getMaximizedTabId = useCallback((): string | null => {
    if (!layoutState.maximizedLayout) return null
    return getMaximizedTabIdFromLayout(layoutState.maximizedLayout)
  }, [layoutState.maximizedLayout])
  
  const isMaximized = useCallback((): boolean => {
    return layoutState.maximizedLayout !== null
  }, [layoutState.maximizedLayout])
  
  // ============================================================================
  // LAYOUT SETTERS
  // ============================================================================
  
  /** Set active panel - triggers re-render for CSS border highlight */
  const setActivePanel = useCallback((panelId: string | null) => {
    setLayoutState(prev => ({ ...prev, activePanelId: panelId }))
  }, [setLayoutState])
  
  /**
   * Core layout update function with URL sync.
   * Updates layout state, active panel, and syncs URL (only if changed).
   */
  const setLayoutStateWithSync = useCallback((
    newLayout: LayoutBase,
    currentTabId?: string,
    direction?: DropDirection
  ) => {
    // Check for empty tabs and reset to default
    const realTabs = getAllTabIdsFromLayout(newLayout).filter(id => id !== WELCOME_TAB_ID)
    if (realTabs.length === 0) {
      newLayout = getDefaultLayout()
    }

    // Figure out active tab
    // When removing, currentTabId is the removed tab - use layout's activeId instead
    const activeTabId = direction === 'remove'
      ? getActiveTabIdFromLayout(newLayout)
      : (currentTabId ?? getActiveTabIdFromLayout(newLayout))
    
    // Figure out active panel from active tab
    let activePanelId: string | null = null
    if (activeTabId) {
      const panel = findPanelByTabId(newLayout, activeTabId)
      activePanelId = panel?.id ?? null
    }
    
    // Update layout state and active panel together
    setLayoutState(prev => {
      const layoutUpdate = prev.maximizedLayout
        ? { maximizedLayout: newLayout }
        : { normalLayout: newLayout }

      return { ...prev, ...layoutUpdate, ...( activePanelId !== null ? { activePanelId } : {}) }
    })
    
    // Compute target path and navigate if needed
    let targetPath = "/"
    if (activeTabId) {
      const { type } = parseTabId(activeTabId)
      targetPath = type !== "welcome" ? tabIdToPath(activeTabId) : "/"
    }
    
    // Only navigate if URL actually changed
    if (window.location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [setLayoutState, navigate])
  
  /** 
   * setLayout - called by rc-dock's onLayoutChange
   * Updates state so React re-renders with new layout
   */
  const setLayout = useCallback((
    newLayout: LayoutBase,
    currentTabId?: string,
    direction?: DropDirection
  ) => {
    setLayoutStateWithSync(newLayout, currentTabId, direction)
  }, [setLayoutStateWithSync])
  
  // ============================================================================
  // MAXIMIZE / RESTORE
  // ============================================================================
  
  const maximize = useCallback((tabId: string) => {
    // Already maximized? Do nothing
    if (layoutState.maximizedLayout) return
    
    const panel = findPanelByTabId(layoutState.normalLayout, tabId)
    if (!panel?.id) return
    
    // Create maximized layout, normalLayout stays untouched
    const maximized = maximizePanel(layoutState.normalLayout, panel.id)
    setLayoutState(prev => ({ ...prev, maximizedLayout: maximized }))
  }, [layoutState.maximizedLayout, layoutState.normalLayout, setLayoutState])
  
  const restoreMaximized = useCallback(() => {
    // Just clear maximizedLayout, normalLayout is already preserved
    setLayoutState(prev => ({ ...prev, maximizedLayout: null }))
  }, [setLayoutState])
  
  // ============================================================================
  // METADATA
  // ============================================================================
  
  const getTabMetadata = useCallback((tabId: string): TabMetadata | undefined => {
    return metadataRef.current.get(tabId)
  }, [])
  
  const setTabMetadata = useCallback((tabId: string, meta: TabMetadata) => {
    metadataRef.current.set(tabId, meta)
    metadataEvents.emit[tabId].updated(meta)
  }, [metadataEvents])
  
  // ============================================================================
  // LAYOUT QUERIES (thin wrappers around layoutUtils)
  // ============================================================================
  
  const findTab = useCallback((tabId: string): TabData | null => {
    return findTabById(getLayout(), tabId)
  }, [getLayout])
  
  const findPanel = useCallback((tabId: string): PanelData | null => {
    return findPanelByTabId(getLayout(), tabId)
  }, [getLayout])
  
  const getActiveTabId = useCallback((): string | null => {
    return getActiveTabIdFromLayout(getLayout())
  }, [getLayout])
  
  const getAllTabIds = useCallback((): string[] => {
    return getAllTabIdsFromLayout(getLayout())
  }, [getLayout])
  
  const isTabOpen = useCallback((tabId: string): boolean => {
    return isTabOpenInLayout(getLayout(), tabId)
  }, [getLayout])
  
  // ============================================================================
  // TAB OPERATIONS
  // ============================================================================
  
  const open = useCallback((type: TabType, entityId?: string, options?: TabOpenOptions) => {
    const tabId = makeTabId(type, entityId)
    
    // Get current layout (normal, since we operate on normalLayout for tab management)
    const currentLayout = layoutState.normalLayout
    
    let newLayout: LayoutBase

    if (options) {
      // If tab already exists anywhere, just activate it
      if (isTabOpenInLayout(currentLayout, tabId)) {
        newLayout = setActiveTab(currentLayout, tabId)
        setLayoutStateWithSync(newLayout, tabId)
        return
      }

      const newTab: TabBase = { id: tabId }

      // Check for reuse — is there already a panel on the target edge?
      if (options.reuseExisting) {
        const sibling = findSiblingPanel(currentLayout, options.relativeTo, options.placement)
        if (sibling?.id) {
          newLayout = addTabToPanel(currentLayout, newTab, sibling.id)
          newLayout = setActiveTab(newLayout, tabId)
          setLayoutStateWithSync(newLayout, tabId)
          return
        }
      }

      // No reuse — create split
      newLayout = splitPanel(currentLayout, options.relativeTo, newTab, options.placement, options.scope)
      newLayout = setActiveTab(newLayout, tabId)

      // Remove welcome tab if needed
      if (type !== "welcome" && isTabOpenInLayout(newLayout, WELCOME_TAB_ID)) {
        newLayout = removeTab(newLayout, WELCOME_TAB_ID)
      }

      setLayoutStateWithSync(newLayout, tabId)
      return
    }

    // If tab exists, just activate it
    if (isTabOpenInLayout(currentLayout, tabId)) {
      newLayout = setActiveTab(currentLayout, tabId)
    } else {
      // Create new tab, add to active panel (or first panel)
      // Note: TabBase only needs id - loadTab fills in title/content
      const newTab: TabBase = { id: tabId }
      const targetPanelId = layoutState.activePanelId || findFirstPanel(currentLayout)?.id
      
      if (!targetPanelId) return
      
      newLayout = addTabToPanel(currentLayout, newTab, targetPanelId)
      newLayout = setActiveTab(newLayout, tabId)
      
      // Remove welcome tab if this is first real tab
      if (type !== "welcome" && isTabOpenInLayout(newLayout, WELCOME_TAB_ID)) {
        newLayout = removeTab(newLayout, WELCOME_TAB_ID)
      }
    }
    
    // Update layout and sync URL (URL check happens inside)
    setLayoutStateWithSync(newLayout, tabId)
  }, [layoutState.normalLayout, layoutState.activePanelId, setLayoutStateWithSync])
  
  const close = useCallback((tabId: string) => {
    // Call cleanup callback if registered
    const meta = metadataRef.current.get(tabId)
    if (meta?.onClose) {
      meta.onClose()
    }
    // Clean up metadata entry
    metadataRef.current.delete(tabId)
    
    let newLayout = removeTab(layoutState.normalLayout, tabId)
    
    // If no real tabs left, add welcome tab
    const realTabs = getAllTabIdsFromLayout(newLayout).filter(id => id !== WELCOME_TAB_ID)
    if (realTabs.length === 0) {
      newLayout = getDefaultLayout()
    }
    
    // Update layout and sync URL (URL check happens inside)
    setLayoutStateWithSync(newLayout)
  }, [layoutState.normalLayout, setLayoutStateWithSync])
  
  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  // Mark loading done on mount
  useEffect(() => {
    setLoading(false)
  }, [])
  
  // ============================================================================
  // URL SYNC
  // ============================================================================
  
  // Watch for URL changes and sync layout accordingly
  useEffect(() => {
    if (routeType) {
      // URL has a target - open/focus that tab
      open(routeType as TabType, routeId)
    } else {
      // At "/" - sync URL from stored layout's active tab
      const activeTabId = getActiveTabIdFromLayout(layoutState.normalLayout)
      if (activeTabId) {
        const { type } = parseTabId(activeTabId)
        if (type !== "welcome") {
          navigate(tabIdToPath(activeTabId), { replace: true })
        }
        // If welcome, URL stays at "/"
      }
    }
  }, [routeType, routeId])
  
  // ============================================================================
  // API
  // ============================================================================
  
  const api = useMemo<TabsContextType>(() => ({
    loading,
    events,
    
    // Layout getters
    getLayout,
    getActivePanelId,
    getMaximizedTabId,
    isMaximized,
    
    // Layout manipulation
    open,
    close,
    setLayout,
    setActivePanel,
    
    // Maximize
    maximize,
    restoreMaximized,
    
    // Layout queries
    findTab,
    findPanel,
    getActiveTabId,
    getAllTabIds,
    isTabOpen,
    
    // Metadata
    metadataEvents,
    getTabMetadata,
    setTabMetadata,
  }), [
    loading, events,
    getLayout, getActivePanelId, getMaximizedTabId, isMaximized,
    open, close, setLayout, setActivePanel,
    maximize, restoreMaximized,
    findTab, findPanel, getActiveTabId, getAllTabIds, isTabOpen,
    metadataEvents, getTabMetadata, setTabMetadata
  ])



  return (
    <TabsContext.Provider value={api}>
      {children}
    </TabsContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access tabs state and methods.
 * 
 * @example
 * const tabs = useTabs()
 * tabs.open("workspace", "abc123")
 * tabs.close("workspace:abc123")
 * 
 * // For components that work in both old and new context systems:
 * const tabs = useTabs(false) // returns undefined if not in provider
 */
export function useTabs(throwIfMissing?: true): TabsContextType
export function useTabs(throwIfMissing: false): TabsContextType | undefined
export function useTabs(throwIfMissing = true): TabsContextType | undefined {
  const ctx = useContext(TabsContext)
  if (!ctx && throwIfMissing) {
    throw new Error("useTabs must be used within a TabsProvider")
  }
  return ctx ?? undefined
}

// ============================================================================
// DUAL-CONTEXT HOOKS
// ============================================================================

/**
 * Hook to get the active workspace ID, supporting both old and new context systems.
 * 
 * Use this in components that need to work in both /chat (WorkspaceContext) 
 * and /chat2 (TabsContext).
 * 
 * @returns The active workspace ID, or undefined if no workspace is active
 * 
 * @example
 * const workspaceId = useActiveWorkspaceId()
 * if (workspaceId) {
 *   // Do something with the workspace
 * }
 */
export function useActiveWorkspaceId(): string | undefined {
  // Try new tabs context first (returns undefined if not in provider)
  const tabs = useTabs(false)
  
  // New system: parse "workspace:abc123" format from active tab
  const activeTabId = tabs?.getActiveTabId()
  if (activeTabId) {
    const parsed = parseTabId(activeTabId)
    if (parsed.type === "workspace") {
      return parsed.entityId
    }
  }
  
  return undefined
}

// ============================================================================
// EXPORTS
// ============================================================================

export { makeTabId, parseTabId }
